import { z } from 'zod';
import { Patient } from '../types/patient';
import { PatientStudy } from '../types/patientStudy';
import { storageService, WorkflowDraft } from './unifiedStorageService';

export const PORTABLE_BUNDLE_VERSION = 1 as const;

const patientSchema = z.object({
  id: z.string(),
  firstName: z.string(),
  lastName: z.string(),
}).passthrough();

const workflowDraftSchema = z.object({
  id: z.string(),
  patientId: z.string(),
  currentStep: z.string(),
  updatedAt: z.string(),
  createdAt: z.string(),
}).passthrough();

const portableBundleSchema = z.object({
  version: z.literal(PORTABLE_BUNDLE_VERSION),
  exportedAt: z.string(),
  patients: z.array(patientSchema),
  studies: z.array(z.object({ id: z.string() }).passthrough()),
  drafts: z.array(workflowDraftSchema).optional(),
});

export interface EdxPortableBundle {
  version: typeof PORTABLE_BUNDLE_VERSION;
  exportedAt: string;
  patients: Patient[];
  studies: PatientStudy[];
  drafts?: WorkflowDraft[];
}

export type ImportMode = 'merge' | 'replace';

export interface ImportResult {
  patientsImported: number;
  studiesImported: number;
  draftsImported: number;
}

function mergeById<T extends { id: string }>(existing: T[], incoming: T[]): T[] {
  const map = new Map(existing.map((item) => [item.id, item]));
  for (const item of incoming) {
    map.set(item.id, item);
  }
  return Array.from(map.values());
}

export function exportPortableBundle(): EdxPortableBundle {
  return {
    version: PORTABLE_BUNDLE_VERSION,
    exportedAt: new Date().toISOString(),
    patients: storageService.getAllPatients(),
    studies: storageService.getAllPatientStudies(),
    drafts: storageService.getAllWorkflowDrafts(),
  };
}

export function serializePortableBundle(bundle: EdxPortableBundle): string {
  return JSON.stringify(bundle, null, 2);
}

export function parsePortableBundle(jsonData: string): EdxPortableBundle {
  const parsed = JSON.parse(jsonData);
  const validated = portableBundleSchema.parse(parsed);
  return validated as unknown as EdxPortableBundle;
}

export async function importPortableBundle(
  jsonData: string,
  mode: ImportMode = 'merge',
): Promise<ImportResult> {
  const bundle = parsePortableBundle(jsonData);

  if (mode === 'replace') {
    await storageService.replaceAllPatients(bundle.patients);
    await storageService.replaceAllPatientStudies(bundle.studies);
    await storageService.replaceAllWorkflowDrafts(bundle.drafts ?? []);
    return {
      patientsImported: bundle.patients.length,
      studiesImported: bundle.studies.length,
      draftsImported: bundle.drafts?.length ?? 0,
    };
  }

  const mergedPatients = mergeById(storageService.getAllPatients(), bundle.patients);
  const mergedStudies = mergeById(storageService.getAllPatientStudies(), bundle.studies);
  const mergedDrafts = mergeById(storageService.getAllWorkflowDrafts(), bundle.drafts ?? []);

  await storageService.replaceAllPatients(mergedPatients);
  await storageService.replaceAllPatientStudies(mergedStudies);
  await storageService.replaceAllWorkflowDrafts(mergedDrafts);

  return {
    patientsImported: bundle.patients.length,
    studiesImported: bundle.studies.length,
    draftsImported: bundle.drafts?.length ?? 0,
  };
}

export function downloadPortableBundle(filename?: string): void {
  const bundle = exportPortableBundle();
  const blob = new Blob([serializePortableBundle(bundle)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename ?? `edx-backup-${new Date().toISOString().slice(0, 10)}.json`;
  anchor.click();
  URL.revokeObjectURL(url);
}

export async function parsePortableBundleFile(file: File): Promise<EdxPortableBundle> {
  const text = await file.text();
  return parsePortableBundle(text);
}

export function getLocalDataSummary(): {
  patientCount: number;
  studyCount: number;
  draftCount: number;
} {
  return {
    patientCount: storageService.getAllPatients().length,
    studyCount: storageService.getAllPatientStudies().length,
    draftCount: storageService.getAllWorkflowDrafts().length,
  };
}

export function getRecentWorkflowDraft(maxAgeDays = 7): WorkflowDraft | null {
  const draft = storageService.getLatestWorkflowDraft();
  if (!draft) return null;

  const ageMs = Date.now() - new Date(draft.updatedAt).getTime();
  const maxAgeMs = maxAgeDays * 24 * 60 * 60 * 1000;
  return ageMs <= maxAgeMs ? draft : null;
}
